import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert } from 'react-bootstrap';
import api from '../../../utils/api';
import { PASSWORD_HELP_TEXT, describePasswordProblems } from '../../../utils/passwordPolicy';
import { AuthShell } from './AuthShell';
import { YButton } from '../../../ui/YButton';
import { YFormField } from '../../../ui/YFormField';
import { AuthPasswordInput } from './AuthPasswordInput';
import '../styles/auth-design.css';

export function ResetPasswordPage() {
  const { token } = useParams();
  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!token) {
    return (
      <AuthShell className="auth-forgot-shell">
        <div className="auth-panel">
          <div className="auth-panel__heading">
            <h1 className="auth-panel__title">Choose a new password</h1>
          </div>
          <Alert variant="danger" className="mb-3">
            This reset link is invalid.
          </Alert>
          <p className="auth-switch-line">
            <Link to="/forgot-password" className="auth-forgot-link">
              Request a new link
            </Link>
          </p>
        </div>
      </AuthShell>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const next = {};
    const passwordProblem = describePasswordProblems(password1);
    if (passwordProblem) next.password1 = passwordProblem;
    if (password2 !== password1) next.password2 = "Passwords don't match";
    setFieldErrors(next);
    if (Object.keys(next).length) return;

    setLoading(true);
    try {
      await api.post('/auth/password-reset/confirm/', {
        token,
        password1,
        password2,
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not reset password. Request a new link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell className="auth-forgot-shell">
      <div className="auth-panel">
        <div className="auth-panel__heading">
          <h1 className="auth-panel__title">Choose a new password</h1>
          <p className="auth-panel__subtitle">
            {done
              ? 'Your password has been updated.'
              : 'Use at least 12 characters, with upper and lower case, a number, and a special character.'}
          </p>
        </div>

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        {done && (
          <Alert variant="success" className="mb-3">
            Password updated. You can now sign in.
          </Alert>
        )}

        {!done && (
          <form className="auth-form-stack" onSubmit={handleSubmit} noValidate>
            <YFormField
              label="New password"
              htmlFor="reset-pw1"
              error={fieldErrors.password1}
              help={!fieldErrors.password1 ? PASSWORD_HELP_TEXT : undefined}
            >
              <AuthPasswordInput
                id="reset-pw1"
                value={password1}
                onChange={(e) => setPassword1(e.target.value)}
                placeholder="Create a password"
                disabled={loading}
                invalid={!!fieldErrors.password1}
              />
            </YFormField>
            <YFormField
              label="Confirm password"
              htmlFor="reset-pw2"
              error={fieldErrors.password2}
            >
              <AuthPasswordInput
                id="reset-pw2"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                placeholder="Repeat password"
                disabled={loading}
                invalid={!!fieldErrors.password2}
              />
            </YFormField>
            <YButton type="submit" variant="primary" block disabled={loading}>
              {loading ? 'Saving…' : 'Update password'}
            </YButton>
          </form>
        )}

        <p className="auth-switch-line">
          <Link to="/" className="auth-forgot-link">
            Back to sign in
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
